/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// ============================================
// DOS518 - Fraseologia ICAO Component
// Modulo D - Comunicazioni Radio Standard
// VERSIONE CON LOG COMUNICAZIONI INTEGRATO
// ============================================

import React, { useState, useRef } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { AvvicinamentoModal } from './AvvicinamentoModal';
import { ComunicazioniSicurezzaModal } from './ComunicazioniSicurezzaModal';
import { ComunicazioniOperativeModal } from './ComunicazioniOperativeModal';
import { LogComunicazioniModal } from './LogComunicazioniModal';
import {
  toPhonetic,
  formatTimeForRadio,
  generateCommLog,
} from '../../utils/fraseologia';
import './Fraseologia.css';

export const Fraseologia: React.FC = () => {
  const coau = useAppStore((state) => state.coau);
  const addMessaggio = useAppStore((state) => state.addMessaggio);
  const messaggiRadio = useAppStore((state) => state.messaggiRadio);
  const logComunicazioni = useAppStore((state) => state.logComunicazioni);

  // Stati per i modali
  const [showAvvicinamentoModal, setShowAvvicinamentoModal] = useState(false);
  const [showComunicazioniModal, setShowComunicazioniModal] = useState(false);
  const [showComunicazioniOperativeModal, setShowComunicazioniOperativeModal] = useState(false);
  const [showAlfabetoModal, setShowAlfabetoModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showLogComunicazioniModal, setShowLogComunicazioniModal] = useState(false);

  // Stati per Alfabeto
  const [phoneticInput, setPhoneticInput] = useState<string>('');
  const [phoneticOutput, setPhoneticOutput] = useState<string>('');

  const logRef = useRef<HTMLDivElement>(null);

  // Handlers Templates
  const handleConvertToPhonetic = () => {
    if (!phoneticInput.trim()) return;
    setPhoneticOutput(toPhonetic(phoneticInput));
  };

  return (
    <div className="fraseologia-manager">
      {/* Header centrato - Prima riga */}
      <div className="fraseologia-header-centered">
        <h2>📢 Fraseologia ICAO</h2>
      </div>

      {/* Bottoni - Seconda riga */}
      <div className="fraseologia-buttons">
        <button
          className="fraseologia-btn"
          onClick={() => setShowAvvicinamentoModal(true)}
        >
          🛬 Arrivo Aereo
        </button>
        <button
          className="fraseologia-btn"
          onClick={() => setShowComunicazioniModal(true)}
        >
          📡 Comunicazioni di Sicurezza
        </button>
        <button
          className="fraseologia-btn"
          onClick={() => setShowComunicazioniOperativeModal(true)}
        >
          📡 Comunicazioni Operative
        </button>
        <button
          className="fraseologia-btn"
          onClick={() => setShowAlfabetoModal(true)}
        >
          🔤 Alfabeto Fonetico
        </button>
        <button
          className="fraseologia-btn btn-log"
          onClick={() => setShowLogComunicazioniModal(true)}
        >
          📋 Log Comunicazioni ({logComunicazioni.length})
        </button>
      </div>

      {/* MODALE AVVICINAMENTO */}
      <AvvicinamentoModal 
        isOpen={showAvvicinamentoModal}
        onClose={() => setShowAvvicinamentoModal(false)}
      />

      {/* MODALE COMUNICAZIONI DI SICUREZZA */}
      <ComunicazioniSicurezzaModal 
        isOpen={showComunicazioniModal}
        onClose={() => setShowComunicazioniModal(false)}
      />

      {/* MODALE COMUNICAZIONI OPERATIVE */}
      <ComunicazioniOperativeModal 
        isOpen={showComunicazioniOperativeModal}
        onClose={() => setShowComunicazioniOperativeModal(false)}
      />

      {/* MODALE LOG COMUNICAZIONI - NUOVO */}
      <LogComunicazioniModal
        isOpen={showLogComunicazioniModal}
        onClose={() => setShowLogComunicazioniModal(false)}
      />

      {/* MODALE ALFABETO FONETICO */}
      {showAlfabetoModal && (
        <div className="modal-overlay" onClick={() => setShowAlfabetoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔤 Alfabeto Fonetico ICAO</h3>
              <button className="btn-close" onClick={() => setShowAlfabetoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alfabeto-converter">
                <h4>🔄 Convertitore Fonetico</h4>
                <div className="converter-form">
                  <div className="form-group">
                    <label>Testo da convertire</label>
                    <input
                      type="text"
                      value={phoneticInput}
                      onChange={(e) => setPhoneticInput(e.target.value.toUpperCase())}
                      placeholder="Es: INDIA45 o ALFA123"
                    />
                  </div>
                  <div className="converter-buttons">
                    <button className="btn btn-primary" onClick={handleConvertToPhonetic}>
                      🔄 Converti in Fonetico
                    </button>
                  </div>
                  {phoneticOutput && (
                    <div className="phonetic-output">
                      <label>Risultato:</label>
                      <div className="output-box">
                        <p>{phoneticOutput}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fraseologia;
